import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { existingLoginOf, type DriverGroupView, type DriverRegistryFormView, type ExistingLoginHintView, type ResolvedCatalogCategoryView } from '@vello/api-client';
import { driverRegistryAuthority, emptyRegistrationDraft, packLabel, registrationInput, registrationReady, visibleCountryFields, type RegistrationDraft } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { MarketField } from '../components/market-field';
import { Button, CheckRow, ConfirmDialog, Field, Notice, PageHeader, Panel, Select, TextInput, FormGrid, SectionTitle, Ink, Row, FormStack, Stack, type ConfirmSpec } from '@vello/web-theme';

/**
 * **Motoristas › Cadastrar motorista** (W9-DRIVER-REGISTRY, D481, ADR-0099; DevMobility D-012 "Formulário de cadastro básico
 * do prestador (painel)", I-028) over `GET /v1/admin/driver-registry/form` and `POST /v1/admin/driver-registry` (`driver:register`).
 *
 * - The **market** comes from the operator's scope. Its form names what a registration needs: the neutral core (name, phone,
 *   e-mail, map identifier, group, observation, birth date, address) and, when the market's country pack has them, the
 *   country's own fields, rendered generically from the pack's description (labels in the operator's language).
 * - A **gender** is recorded only with the driver's consent: choosing one makes the consent box required, and nothing else
 *   reads it. The onboarding items the market requires (photo, selfie, e-mail check, payout account, address) are listed so
 *   the operator knows what the record will still ask for; evidence is recorded on the driver's record afterwards.
 * - The one primary action is **Cadastrar motorista**; a created driver opens on his record. Every refusal (a taken phone or
 *   map identifier, a birth date under the country's minimum age, a missing consent, …) is said in words. No native dialog.
 * - D752 (doc 34 K26): a phone that already has a login (a rider, or a provider-app sign-up never finished) answers `PHONE_HAS_LOGIN` with only the
 *   login's masked first name and realms. The screen asks the operator, in our own dialog, to confirm with the person that it is the same person; "É a
 *   mesma pessoa" re-sends the registration with `adoptExistingLogin: true` and the record is made on that login (said in words before leaving).
 */
export function DriverRegisterScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const { t, lang } = useT();
  const { v, refusal: refusalOf } = useVocab();
  const { navigate } = useRouter();
  const { mayRegister } = driverRegistryAuthority(grants);
  const [market, setMarket] = useState('');
  const [form, setForm] = useState<DriverRegistryFormView | undefined>(undefined);
  const [groups, setGroups] = useState<readonly DriverGroupView[]>([]);
  // D723: the market's mobility categories (the invitation code is for one of them) and the outcome of a code the server did not attach.
  const [categories, setCategories] = useState<readonly ResolvedCatalogCategoryView[]>([]);
  const [created, setCreated] = useState<{ readonly driverId: string; readonly reason?: string; readonly adopted: boolean } | undefined>(undefined);
  // D752 (doc 34 K26): the login the phone already has, while the operator confirms it is the same person.
  const [existingLogin, setExistingLogin] = useState<ExistingLoginHintView | undefined>(undefined);
  const [draft, setDraft] = useState<RegistrationDraft>(() => emptyRegistrationDraft(lang));
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const loadForm = useCallback(
    async (m: string): Promise<void> => {
      const n = ++seq.current;
      setError(undefined);
      setForm(undefined);
      setGroups([]);
      if (m === '') return;
      const [res, grp, cats] = await Promise.all([runtime.driverRegistry.form(m), runtime.wallet.groups(m), runtime.serviceCategories.market(m)]);
      if (n !== seq.current) return;
      setCategories(cats.ok ? cats.value.categories.filter((c) => c.status === 'ACTIVE' && c.kind === 'mobility') : []);
      if (res.ok) setForm(res.value);
      else setError(refusalOf('refusal.admin.driver_registry', res.error) ?? t('registry.refusal.UNKNOWN'));
      if (grp.ok) setGroups(grp.value.groups.filter((g) => g.status === 'ACTIVE'));
    },
    [runtime, t],
  );

  useEffect(() => {
    void loadForm(market);
  }, [market, loadForm]);

  const set = (patch: Partial<RegistrationDraft>): void => setDraft((d) => ({ ...d, ...patch }));
  const shown = form?.countryFields !== undefined ? visibleCountryFields(form.countryFields.fields, draft.attributes) : [];
  const required = form?.requirements.driver.required ?? [];
  const ready = form !== undefined && registrationReady(draft, form);

  const submit = async (adoptExistingLogin = false): Promise<void> => {
    if (form === undefined || !ready) return;
    setBusy(true);
    setError(undefined);
    setCreated(undefined);
    const res = await runtime.driverRegistry.register({ ...registrationInput(market, draft, form), ...(adoptExistingLogin ? { adoptExistingLogin: true } : {}) });
    setBusy(false);
    if (!res.ok) {
      setError(refusalOf('refusal.admin.driver_registry', res.error) ?? t('registry.refusal.UNKNOWN'));
      // D752 (K26): the phone already has a login - ask whether it is the same person (nothing was written).
      const hint = adoptExistingLogin ? undefined : existingLoginOf(res.error);
      if (hint !== undefined) setExistingLogin(hint);
      return;
    }
    const adopted = res.value.adopted === true;
    // D723: the driver exists either way; a code the server did not attach is said in words before leaving the form. D752: so is an adopted login.
    const notAttached = res.value.referral !== undefined && !res.value.referral.ok ? v('problem.driver_referral', res.value.referral.reasonCode) : undefined;
    if (notAttached !== undefined || adopted) {
      setCreated({ driverId: res.value.driverId, adopted, ...(notAttached !== undefined ? { reason: notAttached } : {}) });
      return;
    }
    navigate('driver_detail', { id: res.value.driverId });
  };

  const account = existingLogin !== undefined ? [existingLogin.firstNameMasked, existingLogin.realms.map((r) => v('identity.realm', r)).join(', ')].filter((x) => x !== undefined && x !== '').join(' · ') : '';
  const adoptSpec: ConfirmSpec | undefined =
    existingLogin === undefined
      ? undefined
      : {
          title: t('registry.adopt.title'),
          body: account !== '' ? t('registry.adopt.body', { account }) : t('registry.adopt.bodyPlain'),
          confirmLabel: t('registry.adopt.confirm'),
          cancelLabel: t('registry.adopt.cancel'),
          icon: 'userSearch',
        };

  return (
    <>
      <PageHeader title={t('registry.form.title')} subtitle={t('registry.form.subtitle')} />
      {!mayRegister && <Notice tone="neutral">{t('registry.form.noRegister')}</Notice>}

      <Panel>
        <FormGrid>
          <MarketField id="regf-market" value={market} onChange={setMarket} />
        </FormGrid>
        {market === '' && <Notice tone="neutral">{t('registry.marketHint')}</Notice>}
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
        {created !== undefined && (
          <Stack gap={2}>
            {created.adopted && <Notice tone="success">{t('registry.adopt.done')}</Notice>}
            {created.reason !== undefined && <Notice tone="warning">{t('ref.registry.notAttached', { reason: created.reason })}</Notice>}
            <Row gap={2}>
              <Button type="button" variant="secondary" onClick={() => navigate('driver_detail', { id: created.driverId })}>{t('ref.registry.open')}</Button>
            </Row>
          </Stack>
        )}
        {form !== undefined && required.length > 0 && (
          <Notice tone="neutral">{t('registry.form.requirements', { items: required.map((i) => v('driver.onboarding_item', i)).join(', ') })}</Notice>
        )}
      </Panel>

      {form !== undefined && (
        <FormStack gap={6}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Panel><Stack gap={4}>
            <SectionTitle>{t('registry.form.account')}</SectionTitle>
            <Ink as="p" tone="secondary">{t('registry.form.account.hint')}</Ink>
            <FormGrid>
              <Field label={t('registry.field.name')} htmlFor="regf-name">
                <TextInput id="regf-name" value={draft.name} autoComplete="off" onChange={(e) => set({ name: e.target.value })} />
              </Field>
              <Field label={t('registry.field.phone')} htmlFor="regf-phone" hint={t('registry.field.phone.hint')}>
                <TextInput id="regf-phone" className="tnum" type="tel" value={draft.phone} autoComplete="off" onChange={(e) => set({ phone: e.target.value })} />
              </Field>
              <Field label={t('registry.field.email')} htmlFor="regf-email">
                <TextInput id="regf-email" type="email" value={draft.email} autoComplete="off" onChange={(e) => set({ email: e.target.value })} />
              </Field>
              <Field label={t('registry.field.locale')} htmlFor="regf-locale" hint={t('registry.field.locale.hint')}>
                <Select id="regf-locale" value={draft.locale} onChange={(e) => set({ locale: e.target.value })}>
                  {['pt', 'en', 'es', 'it'].map((l) => (
                    <option key={l} value={l}>{v('common.language', l)}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t('registry.field.birthDate')} htmlFor="regf-birth" hint={form.countryFields?.minimumAgeYears !== undefined ? t('registry.field.birthDate.min', { years: String(form.countryFields.minimumAgeYears) }) : undefined}>
                <TextInput id="regf-birth" className="tnum" type="date" max={form.today} value={draft.birthDate} onChange={(e) => set({ birthDate: e.target.value })} />
              </Field>
            </FormGrid>
          </Stack></Panel>

          <Panel>
            <SectionTitle>{t('registry.form.operation')}</SectionTitle>
            <FormGrid>
              <Field label={t('registry.field.mapIdentifier')} htmlFor="regf-map" hint={t('registry.field.mapIdentifier.hint')}>
                <TextInput id="regf-map" className="tnum" value={draft.mapIdentifier} autoComplete="off" onChange={(e) => set({ mapIdentifier: e.target.value })} />
              </Field>
              <Field label={t('registry.field.group')} htmlFor="regf-group">
                <Select id="regf-group" value={draft.groupId} onChange={(e) => set({ groupId: e.target.value })}>
                  <option value="">{t('registry.field.group.none')}</option>
                  {groups.map((g) => (
                    <option key={g.groupId} value={g.groupId}>{g.name}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t('registry.field.observation')} htmlFor="regf-obs">
                <TextInput id="regf-obs" value={draft.observation} maxLength={1000} onChange={(e) => set({ observation: e.target.value })} />
              </Field>
              {/* D723: the code of the rider who invited him ("Convide motoristas") - optional; the category it is for comes from the market's catalog. */}
              <Field label={t('ref.registry.code')} htmlFor="regf-referral" hint={t('ref.registry.codeHint')}>
                <TextInput id="regf-referral" className="tnum" value={draft.referralCode} maxLength={12} autoComplete="off" onChange={(e) => set({ referralCode: e.target.value })} />
              </Field>
              {draft.referralCode.trim() !== '' && (
                <Field label={t('ref.registry.category')} htmlFor="regf-referral-category">
                  <Select id="regf-referral-category" value={draft.referralCategoryCode} onChange={(e) => set({ referralCategoryCode: e.target.value })}>
                    <option value="">{t('registry.choose')}</option>
                    {categories.map((c) => (
                      <option key={c.code} value={c.code}>{packLabel(c.labels, lang, c.code)}</option>
                    ))}
                  </Select>
                </Field>
              )}
            </FormGrid>
          </Panel>

          <Panel>
            <SectionTitle>{t('registry.form.address')}</SectionTitle>
            <FormGrid>
              <Field label={t('registry.field.line1')} htmlFor="regf-line1">
                <TextInput id="regf-line1" value={draft.line1} autoComplete="off" onChange={(e) => set({ line1: e.target.value })} />
              </Field>
              <Field label={t('registry.field.line2')} htmlFor="regf-line2">
                <TextInput id="regf-line2" value={draft.line2} autoComplete="off" onChange={(e) => set({ line2: e.target.value })} />
              </Field>
              <Field label={t('registry.field.locality')} htmlFor="regf-locality">
                <TextInput id="regf-locality" value={draft.locality} autoComplete="off" onChange={(e) => set({ locality: e.target.value })} />
              </Field>
              <Field label={t('registry.field.region')} htmlFor="regf-region">
                <TextInput id="regf-region" value={draft.region} autoComplete="off" onChange={(e) => set({ region: e.target.value })} />
              </Field>
              <Field label={t('registry.field.postalCode')} htmlFor="regf-postal">
                <TextInput id="regf-postal" className="tnum" value={draft.postalCode} autoComplete="off" onChange={(e) => set({ postalCode: e.target.value })} />
              </Field>
            </FormGrid>
          </Panel>

          {form.countryFields !== undefined && (
            <Panel><Stack gap={4}>
              <SectionTitle>{t('registry.form.country', { country: form.country })}</SectionTitle>
              <Ink as="p" tone="secondary">{t('registry.form.country.hint')}</Ink>
              <FormGrid>
                {shown.map((f) => (
                  <Field key={f.key} label={`${packLabel(f.labels, lang, f.key)}${f.required ? '' : ` (${t('registry.optional')})`}`} htmlFor={`regf-attr-${f.key}`} hint={f.hint !== undefined ? packLabel(f.hint, lang, '') : undefined}>
                    {f.input === 'CHOICE' ? (
                      <Select id={`regf-attr-${f.key}`} value={draft.attributes[f.key] ?? ''} onChange={(e) => set({ attributes: { ...draft.attributes, [f.key]: e.target.value } })}>
                        <option value="">{t('registry.choose')}</option>
                        {(f.options ?? []).map((o) => (
                          <option key={o.value} value={o.value}>{packLabel(o.labels, lang, o.value)}</option>
                        ))}
                      </Select>
                    ) : (
                      <TextInput
                        id={`regf-attr-${f.key}`}
                        className="tnum"
                        value={draft.attributes[f.key] ?? ''}
                        maxLength={f.maxLength}
                        inputMode={f.input === 'DIGITS' ? 'numeric' : undefined}
                        autoComplete="off"
                        onChange={(e) => set({ attributes: { ...draft.attributes, [f.key]: e.target.value } })}
                      />
                    )}
                  </Field>
                ))}
              </FormGrid>
            </Stack></Panel>
          )}

          <Panel><Stack gap={4}>
            <SectionTitle>{t('registry.form.gender')}</SectionTitle>
            <Ink as="p" tone="secondary">{t('registry.form.gender.hint')}</Ink>
            <FormGrid>
              <Field label={t('registry.field.gender')} htmlFor="regf-gender">
                <Select id="regf-gender" value={draft.gender} onChange={(e) => set({ gender: e.target.value as RegistrationDraft['gender'], genderConsent: e.target.value === '' ? false : draft.genderConsent })}>
                  <option value="">{t('registry.field.gender.none')}</option>
                  {form.genders.map((g) => (
                    <option key={g} value={g}>{v('driver.gender', g)}</option>
                  ))}
                </Select>
              </Field>
              {draft.gender !== '' && (
                <div>
                  <CheckRow id="regf-gender-consent" checked={draft.genderConsent} onChange={(on) => set({ genderConsent: on })}>{t('registry.field.genderConsent')}</CheckRow>
                </div>
              )}
            </FormGrid>
          </Stack></Panel>

          <Row gap={2} align="stretch" wrap>
            <Button type="submit" variant="primary" disabled={busy || !mayRegister || !ready}>{t('registry.form.submit')}</Button>
            <Button type="button" variant="ghost" onClick={() => navigate('driver_registry')}>{t('registry.form.cancel')}</Button>
          </Row>
        </FormStack>
      )}
      <ConfirmDialog
        spec={adoptSpec}
        open={existingLogin !== undefined}
        onResolve={(r) => {
          setExistingLogin(undefined);
          if (r.confirmed) void submit(true);
        }}
      />
    </>
  );
}
